import React, { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { vec3 } from 'gl-matrix';
import PropTypes from 'prop-types';
import { metaData, Enums, utilities } from '@cornerstonejs/core';
import { ViewportOverlay } from '@ohif/ui';
import {
  formatPN,
  formatDICOMDate,
  formatDICOMTime,
  formatNumberPrecision,
  isValidNumber,
} from './utils';
import { InstanceMetadata } from 'platform/core/src/types';
import { ServicesManager } from '@ohif/core';
import { ImageSliceData } from '@cornerstonejs/core/dist/esm/types';
import './CustomizableViewportOverlay.css';

import moment from 'moment';

const EPSILON = 1e-4;

interface OverlayItemProps {
  element: any;
  viewportData: any;
  imageSliceData: ImageSliceData;
  viewportIndex: number | null;
  servicesManager: ServicesManager;
  instance: InstanceMetadata;
  customization: any;
  formatters: {
    formatPN: (val) => string;
    formatDate: (val) => string;
    formatTime: (val) => string;
    formatNumberPrecision: (val, number) => string;
  };

  // calculated values
  voi: {
    windowWidth: number;
    windowCenter: number;
  };
  instanceNumber?: number;
  scale?: number;
}

/**
 * Window Level / Center Overlay item
 */
function VOIOverlayItem({ voi, customization }: OverlayItemProps) {
  const { windowWidth, windowCenter } = voi;
  if (typeof windowCenter !== 'number' || typeof windowWidth !== 'number') {
    return null;
  }

  return (
    <div
      className="overlay-item flex flex-row"
      style={{ color: (customization && customization.color) || undefined }}
    >
      <span className="mr-1 shrink-0">W:</span>
      <span className="ml-1 mr-2 font-light shrink-0">
        {windowWidth.toFixed(0)}
      </span>
      <span className="mr-1 shrink-0">L:</span>
      <span className="ml-1 font-light shrink-0">
        {windowCenter.toFixed(0)}
      </span>
    </div>
  );
}

/**
 * Zoom Level Overlay item
 */
function ZoomOverlayItem({ scale, customization }: OverlayItemProps) {
  return (
    <div
      className="overlay-item flex flex-row"
      style={{ color: (customization && customization.color) || undefined }}
    >
      <span className="mr-1 shrink-0">Zoom:</span>
      <span className="font-light">{scale.toFixed(2)}x</span>
    </div>
  );
}

/**
 * Instance Number Overlay Item
 */
function InstanceNumberOverlayItem({
  instanceNumber,
  imageSliceData,
  customization,
}: OverlayItemProps) {
  const { imageIndex, numberOfSlices } = imageSliceData;

  return (
    <div
      className="overlay-item flex flex-row"
      style={{ color: (customization && customization.color) || undefined }}
    >
      <span className="mr-1 shrink-0">I:</span>
      <span className="font-light">
        {instanceNumber !== undefined && instanceNumber !== null
          ? `${instanceNumber} (${imageIndex + 1}/${numberOfSlices})`
          : `${imageIndex + 1}/${numberOfSlices}`}
      </span>
    </div>
  );
}

/**
 * Customizable Viewport Overlay
 */
function CustomizableViewportOverlay({
  element,
  viewportData,
  imageSliceData,
  viewportIndex,
  servicesManager,
  viewportId
}) {
  const {
    toolbarService,
    cornerstoneViewportService,
    customizationService,
  } = servicesManager.services;

  const [voi, setVOI] = useState({ windowCenter: null, windowWidth: null });
  const [scale, setScale] = useState(1);
  const [activeTools, setActiveTools] = useState([]);
  const { imageIndex } = imageSliceData;

  const topLeftCustomization = customizationService.getModeCustomization(
    'cornerstoneOverlayTopLeft'
  );
  const topRightCustomization = customizationService.getModeCustomization(
    'cornerstoneOverlayTopRight'
  );
  const bottomLeftCustomization = customizationService.getModeCustomization(
    'cornerstoneOverlayBottomLeft'
  );
  const bottomRightCustomization = customizationService.getModeCustomization(
    'cornerstoneOverlayBottomRight'
  );

  const instance = useMemo(() => {
    if (viewportData != null) {
      return _getViewportInstance(viewportData, imageIndex);
    } else {
      return null;
    }
  }, [viewportData, imageIndex]);

  const instanceNumber = useMemo(() => {
    if (viewportData != null) {
      return _getInstanceNumber(
        viewportData,
        viewportIndex,
        imageIndex,
        cornerstoneViewportService
      );
    }
    return null;
  }, [viewportData, viewportIndex, imageIndex, cornerstoneViewportService]);


  // Implementado
  const [tagsDicom, setTagsDicom] = useState({
    NomePaciente: "",
    PatientID: "",
    Descricao: "",
    DataImagem: "",
    SerieNumero: "",
    ImageCompression: "",
    ImageDimensions: ""
  });
  const [imagePlaneModule, setImagePlaneModule] = useState({});
  const [frameRate, setFrameRate] = useState(0);

  const getInfoImage = () => {
    try {
      return servicesManager.services.DisplaySetService.getDisplaySetByUID(viewportData.data.displaySetInstanceUID).images[imageSliceData.imageIndex];
    } catch (ex) { console.log(ex) }
  }

  const retTagInfoPatientName = () => {
    try {
      return getInfoImage().PatientName[0].Alphabetic;
    } catch (ex) { console.log(ex) }
  }

  const retTagInfo = (tag) => {
    try {
      return getInfoImage()[tag];
    } catch (ex) { console.log(ex) }
  }

  const retTagInfoDataImagem = () => {
    try {
      const data = getInfoImage().StudyDate;
      const hora = getInfoImage().StudyTime;
      return `${moment(data).format("MMM D, YYYY")} ${moment(hora, 'HH:mm:ss').format('HH:mm:ss')}`;
    } catch (ex) { console.log(ex) }
  }

  const formatNumberPrecision = (number, precision) => {
    if (number !== null) {
      return parseFloat(number).toFixed(precision);
    }
  }

  const retTagInfoCompression = () => {
    try {
      const imageId = getInfoImage().imageId;
      const generalImageModule =
        cornerstone.metaData.get('generalImageModule', imageId) || {};
      const {
        lossyImageCompression,
        lossyImageCompressionRatio,
        lossyImageCompressionMethod,
      } = generalImageModule;

      if (lossyImageCompression === '01' && lossyImageCompressionRatio !== '') {
        const compressionMethod = lossyImageCompressionMethod || 'Lossy: ';
        const compressionRatio = formatNumberPrecision(
          lossyImageCompressionRatio,
          2
        );
        return compressionMethod + compressionRatio + ' : 1';
      }
      return 'Lossless / Uncompressed';
    } catch (ex) { }
  }

  useEffect(() => {
    try {
      if (viewportData === null) return;
      if (viewportData.data.displaySetInstanceUID) {

        const imageId = getInfoImage().imageId;
        const _imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId) || {};
        const { rows, columns, sliceThickness, sliceLocation } = _imagePlaneModule;
        setImagePlaneModule(_imagePlaneModule);

        const cineModule = cornerstone.metaData.get('cineModule', imageId) || {};
        const { frameTime } = cineModule;
        setFrameRate(formatNumberPrecision(1000 / frameTime, 1));

        //setImagePlaneModule
        setTagsDicom({
          NomePaciente: retTagInfoPatientName(),
          PatientID: retTagInfo('PatientID'),
          SerieNumero: retTagInfo('SeriesNumber'),
          Descricao: retTagInfo('SeriesDescription'),
          DataImagem: retTagInfoDataImagem(),
          ImageCompression: retTagInfoCompression(),
          ImageDimensions: `${retTagInfo('Columns')} x ${retTagInfo('Rows')}`
        });

      }
    } catch (ex) { console.log(ex) }
  }, [viewportData])

  /**
   * Initial toolbar state
   */
  useEffect(() => {
    setActiveTools(toolbarService.getActiveTools());
  }, []);

  /**
   * Updating the VOI when the viewport changes its voi
   */
  useEffect(() => {

    const updateVOI = eventDetail => {
      const { range } = eventDetail.detail;

      if (!range) {
        return;
      }

      const { lower, upper } = range;
      const { windowWidth, windowCenter } = utilities.windowLevel.toWindowLevel(
        lower,
        upper
      );

      setVOI({ windowCenter, windowWidth });
    };

    element.addEventListener(Enums.Events.VOI_MODIFIED, updateVOI);

    return () => {
      element.removeEventListener(Enums.Events.VOI_MODIFIED, updateVOI);
    };
  }, [viewportIndex, viewportData, voi, element]);


  /**
   * Updating the scale when the viewport changes its zoom
   */
  useEffect(() => {
    const updateScale = eventDetail => {
      const { previousCamera, camera } = eventDetail.detail;

      if (
        previousCamera.parallelScale !== camera.parallelScale ||
        previousCamera.scale !== camera.scale
      ) {
        const viewport = cornerstoneViewportService.getCornerstoneViewportByIndex(
          viewportIndex
        );

        if (!viewport) {
          return;
        }

        const imageData = viewport.getImageData();

        if (!imageData) {
          return;
        }

        if (camera.scale) {
          setScale(camera.scale);
          return;
        }

        const { spacing } = imageData;
        // convert parallel scale to scale
        const scale =
          (element.clientHeight * spacing[0] * 0.5) / camera.parallelScale;
        setScale(scale);
      }
    };

    element.addEventListener(Enums.Events.CAMERA_MODIFIED, updateScale);

    return () => {
      element.removeEventListener(Enums.Events.CAMERA_MODIFIED, updateScale);
    };
  }, [viewportIndex, viewportData, cornerstoneViewportService, element]);

  /**
   * Updating the active tools when the toolbar changes
   */
  // Todo: this should act on the toolGroups instead of the toolbar state
  useEffect(() => {
    const { unsubscribe } = toolbarService.subscribe(
      toolbarService.EVENTS.TOOL_BAR_STATE_MODIFIED,
      () => {
        setActiveTools(toolbarService.getActiveTools());
      }
    );

    return () => {
      unsubscribe();
    };
  }, [toolbarService]);

  const _renderOverlayItem = useCallback(
    item => {
      const overlayItemProps: OverlayItemProps = {
        element,
        viewportData,
        imageSliceData,
        viewportIndex,
        servicesManager,
        customization: item,
        formatters: {
          formatPN: formatPN,
          formatDate: formatDICOMDate,
          formatTime: formatDICOMTime,
          formatNumberPrecision: formatNumberPrecision,
        },
        instance,
        // calculated
        voi,
        scale,
        instanceNumber
      };

      if (item.customizationType === 'ohif.overlayItem.windowLevel') {
        return <VOIOverlayItem {...overlayItemProps} />;
      } else if (item.customizationType === 'ohif.overlayItem.zoomLevel') {
        return <ZoomOverlayItem {...overlayItemProps} />;
      } else if (item.customizationType === 'ohif.overlayItem.instanceNumber') {
        return <InstanceNumberOverlayItem {...overlayItemProps} />;
      } else {
        const renderItem = customizationService.transform(item);

        if (typeof renderItem.content === 'function') {
          return renderItem.content(overlayItemProps);
        }
      }
    },
    [
      element,
      viewportData,
      imageSliceData,
      viewportIndex,
      servicesManager,
      customizationService,
      instance,
      voi,
      scale,
      instanceNumber,
    ]
  );

  const getTopLeftContent = useCallback(() => {
    return (<div>
      <div id="top-pacienteNome" className="mr-1">{tagsDicom.NomePaciente}</div>
      <div id="top-pacienteId" className="mr-1">{tagsDicom.PatientID}</div>
    </div>);
  }, [imageSliceData]);

  const getBottomLeftContent = useCallback(() => {

    const { imageIndex, numberOfSlices } = imageSliceData;
    if (!viewportData) {
      return;
    }
    let instanceNumber;
    if (viewportData.viewportType === Enums.ViewportType.STACK) {
      instanceNumber = _getInstanceNumberFromStack(viewportData, imageIndex);
    } else if (viewportData.viewportType === Enums.ViewportType.ORTHOGRAPHIC) {
      instanceNumber = _getInstanceNumberFromVolume(
        viewportData,
        imageIndex,
        viewportIndex,
        cornerstoneViewportService
      );
    }
    return (<div>
      <div>
        <span className="mr-1">Ser.</span>
        <span className="ml-1">{tagsDicom.SerieNumero}</span>
      </div>
      <div>
        <span className="mr-1">Img:</span>
        <span className="font-light">
          {`${imageIndex + 1}/${numberOfSlices}`}
        </span>
      </div>


      <div>
        {frameRate >= 0 ? `${formatNumberPrecision(frameRate, 2)} FPS` : ''}
        <div className="mr-1">{tagsDicom.ImageDimensions}</div>
        <div>
          {isValidNumber(imagePlaneModule.sliceLocation)
            ? `Loc: ${formatNumberPrecision(imagePlaneModule.sliceLocation, 2)} mm `
            : ''}
          {imagePlaneModule.sliceThickness
            ? `Thick: ${formatNumberPrecision(imagePlaneModule.sliceThickness, 2)} mm `
            : ''}
        </div>
        {/* <div>{seriesDescription}</div> */}
      </div>
      <div id={`HuValue-${viewportId}`}></div>
    </div>);
  }, [imageSliceData, viewportData, viewportIndex]);

  const getBottomRightContent = useCallback(() => {
    const { windowWidth, windowCenter } = voi;

    if (activeTools.includes('WindowLevel')) {
      if (typeof windowCenter !== 'number' || typeof windowWidth !== 'number') {
        return null;
      }

      return (
        <div>
          <div className="tx-r">
            <span className='mr-1'>Zoom:</span>
            <span>{scale.toFixed(2)}x</span>
          </div>
          <div className="tx-r">
            <span className="mr-1 tx-r">W:</span>
            <span className="ml-1 mr-2 font-light tx-r">{windowWidth.toFixed(0)}</span>
            <span className="mr-1 tx-r">L:</span>
            <span className="ml-1 font-light tx-r">{windowCenter.toFixed(0)}</span>
          </div>
          <div className="tx-r">{tagsDicom.ImageCompression}</div>

        </div>
      );
    }

    return null;

  }, [voi, scale, activeTools]);

  const getTopRightContent = useCallback(() => {
    return (
      <div>
        <div id="top-exame" className="mr-1 tx-r">{tagsDicom.Descricao}</div>
        <div id="top-dataImagem" className="mr-1 tx-r">{tagsDicom.DataImagem}</div>
      </div>
    );
  }, [imageSliceData, viewportData, viewportIndex]);

  if (!viewportData) {
    return null;
  }

  return (
    <ViewportOverlay
      topLeft={getTopLeftContent()}
      topRight={getTopRightContent()}
      bottomLeft={getBottomLeftContent()}
      bottomRight={getBottomRightContent()}
    />
  );
}


function _getViewportInstance(viewportData, imageIndex) {
  let imageId = null;
  if (viewportData.viewportType === Enums.ViewportType.STACK) {
    imageId = viewportData.data.imageIds[imageIndex];
  } else if (viewportData.viewportType === Enums.ViewportType.ORTHOGRAPHIC) {
    const volumes = viewportData.volumes;
    if (volumes && volumes.length == 1) {
      const volume = volumes[0];
      imageId = volume.imageIds[imageIndex];
    }
  }
  return imageId ? metaData.get('instance', imageId) || {} : {};
}

function _getInstanceNumber(
  viewportData,
  viewportIndex,
  imageIndex,
  cornerstoneViewportService
) {
  let instanceNumber;

  if (viewportData.viewportType === Enums.ViewportType.STACK) {
    instanceNumber = _getInstanceNumberFromStack(viewportData, imageIndex);

    if (!instanceNumber && instanceNumber !== 0) {
      return null;
    }
  } else if (viewportData.viewportType === Enums.ViewportType.ORTHOGRAPHIC) {
    instanceNumber = _getInstanceNumberFromVolume(
      viewportData,
      imageIndex,
      viewportIndex,
      cornerstoneViewportService
    );
  }
  return instanceNumber;
}

function _getInstanceNumberFromStack(viewportData, imageIndex) {
  const imageIds = viewportData.data.imageIds;
  const imageId = imageIds[imageIndex];

  if (!imageId) {
    return;
  }

  const generalImageModule = metaData.get('generalImageModule', imageId) || {};
  const { instanceNumber } = generalImageModule;

  const stackSize = imageIds.length;

  if (stackSize <= 1) {
    return;
  }

  return parseInt(instanceNumber);
}

// Since volume viewports can be in any view direction, they can render
// a reconstructed image which don't have imageIds; therefore, no instance and instanceNumber
// Here we check if viewport is in the acquisition direction and if so, we get the instanceNumber
function _getInstanceNumberFromVolume(
  viewportData,
  imageIndex,
  viewportIndex,
  cornerstoneViewportService
) {
  const volumes = viewportData.volumes;

  // Todo: support fusion of acquisition plane which has instanceNumber
  if (!volumes || volumes.length > 1) {
    return;
  }

  const volume = volumes[0];
  const { direction, imageIds } = volume;

  const cornerstoneViewport = cornerstoneViewportService.getCornerstoneViewportByIndex(
    viewportIndex
  );

  if (!cornerstoneViewport) {
    return;
  }

  const camera = cornerstoneViewport.getCamera();
  const { viewPlaneNormal } = camera;
  // checking if camera is looking at the acquisition plane (defined by the direction on the volume)

  const scanAxisNormal = direction.slice(6, 9);

  // check if viewPlaneNormal is parallel to scanAxisNormal
  const cross = vec3.cross(vec3.create(), viewPlaneNormal, scanAxisNormal);
  const isAcquisitionPlane = vec3.length(cross) < EPSILON;

  if (isAcquisitionPlane) {
    const imageId = imageIds[imageIndex];

    if (!imageId) {
      return {};
    }

    const { instanceNumber } =
      metaData.get('generalImageModule', imageId) || {};
    return parseInt(instanceNumber);
  }
}

CustomizableViewportOverlay.propTypes = {
  viewportData: PropTypes.object,
  imageIndex: PropTypes.number,
  viewportIndex: PropTypes.number,
};

export default CustomizableViewportOverlay;
